import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'
import classnames from 'classnames'
import { STEP_STATE_DISABLED } from 'lib/stepper'

import PageTitle from 'components/atoms/PageTitle'
import PageLoading from 'components/atoms/PageLoading'
import Stepper from 'components/atoms/Stepper'
import NotFound from 'components/atoms/NotFound'
import {
  ASSIGNMENT_STEPS_LIST,
  ASSIGNMENT_STEP_PUBLICATION,
  getAssignmentStepUrl,
  getLastAssignementStep,
} from 'components/organisms/Assignment/assignmentLib'
import AssignmentPublication from 'components/organisms/AssignmentPublication'
import AssignmentCarAssignment from 'components/organisms/AssignmentCarAssignment'
import useAssignmentStepNavigation from 'components/organisms/Assignment/useAssignmentNavigation'
import useAssignmentSteps from 'components/organisms/Assignment/useAssignmentSteps'
import PageSessionSelection from 'components/atoms/PageSessionSelection'
import useAssignmentSession from 'components/organisms/Assignment/useAssignmentSession'
import useCarsToAssign from 'components/organisms/Assignment/useCarsToAssign'
import AssignmentSummary from 'components/organisms/AssignmentSummary'

import useSessionsList from 'hooks/useSessionsList'
import useSession from 'hooks/useSession'
import useEntityData from 'hooks/useEntityData'

import { ROLES_SIXT, ROLE_B2C, ROLE_READ_ONLY, useAuth } from '../../auth'

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function AssignmentStepsRouter() {
  const { t } = useTranslation()

  const { id, step } = useParams()
  const [showHeader, setShowHeader] = useState(true)

  const {
    isFetching: isFetchingSessions,
    fetchSessions,
    sessions,
  } = useSessionsList()

  const {
    isFetching: isFetchingSession,
    fetchSession,
    session,
    error,
  } = useSession()

  const {
    isFetching: isFetchingCars,
    isUpdating: isUpdatingCars,
    fetchCarsToAssign,
    refetchCarsToAssign,
    cars,
    error: errorCarsToAssign,
  } = useCarsToAssign()

  const {
    isUpdating: isUpdatingAssignmentSession,
    result: assignmentStarted,
    assignmentEndedAt,
    startAssignment,
    confirmAssignment,
  } = useAssignmentSession()

  const { items: partnerItems, refetch: fetchPartners } = useEntityData({
    limit: 9999,
  })

  const { fetchCurrentAssignmentSession, session: currentActiveSession } =
    useSession()

  const { handleStepValidation, getStepsWithState } = useAssignmentSteps()

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  useEffect(() => {
    fetchSession(id)
  }, [fetchSession, id, assignmentStarted, assignmentEndedAt])

  useEffect(() => {
    if (!sessions.length) {
      return
    }
    fetchCurrentAssignmentSession()
  }, [fetchCurrentAssignmentSession, sessions])

  useEffect(() => {
    if (!session?.sessionId || step === ASSIGNMENT_STEP_PUBLICATION) {
      return
    }

    fetchCarsToAssign(session?.sessionId)
  }, [fetchCarsToAssign, session?.sessionId, step])

  const refetchList = useCallback(() => {
    refetchCarsToAssign(session?.sessionId)
  }, [session, refetchCarsToAssign])

  useEffect(() => {
    if (!session?.sessionId) {
      return
    }

    fetchPartners(`sessions/${session.sessionId}/partners`)
  }, [fetchPartners, session?.sessionId, step])

  const partners = useMemo(() => {
    return partnerItems.filter((partner) => !!partner.groupId)
  }, [partnerItems])

  const { goToStep } = useAssignmentStepNavigation()

  const steps = useMemo(() => {
    return getStepsWithState(session)
  }, [getStepsWithState, session])

  const readOnly = useMemo(() => {
    return (
      isUpdatingAssignmentSession ||
      isFetchingCars ||
      isFetchingSessions ||
      isFetchingSession ||
      isUpdatingCars
    )
  }, [
    isUpdatingAssignmentSession,
    isFetchingCars,
    isFetchingSessions,
    isFetchingSession,
    isUpdatingCars,
  ])

  const handleStep = (index) => {
    goToStep(session.sessionId, index)
  }

  const handleStartAssignment = () => {
    startAssignment(session.sessionId)
  }

  const { hasOneOfRoles } = useAuth()
  const canAccess = useMemo(() => hasOneOfRoles(ROLES_SIXT), [hasOneOfRoles])
  const userRoleHasReadOnly = useMemo(
    () => hasOneOfRoles([ROLE_READ_ONLY, ROLE_B2C]),
    [hasOneOfRoles]
  )

  if (!canAccess) {
    return <Navigate to="/agenda" />
  }

  return (
    <div className={classnames('content-container', { details: !showHeader })}>
      {isFetchingSessions ||
      (isFetchingSession && !assignmentEndedAt) ||
      isFetchingCars ||
      (!Object.keys(session).length && !error) ||
      !Object.keys(steps).length ? (
        <PageLoading />
      ) : (error && Number(error.response.status) === 404) ||
        (errorCarsToAssign &&
          Number(errorCarsToAssign.response.status) === 404) ? (
        <NotFound />
      ) : !step ||
        !ASSIGNMENT_STEPS_LIST.includes(step) ||
        steps.find((s) => s.step === step).state === STEP_STATE_DISABLED ? (
        <Navigate to={getLastAssignementStep(session.sessionId, steps)} />
      ) : (
        <>
          {(showHeader || step !== ASSIGNMENT_STEPS_LIST[1]) && (
            <PageTitle
              label={t(`assignment.${step}.title`)}
              rightColumn={
                sessions.length && (
                  <PageSessionSelection
                    session={session}
                    currentActiveSession={currentActiveSession}
                    handleStepUrl={getAssignmentStepUrl}
                    sessionsList={sessions}
                    task="assignment"
                    readOnly={readOnly}
                  />
                )
              }
            />
          )}
          {step === ASSIGNMENT_STEPS_LIST[0] ? (
            <>
              <Stepper
                steps={steps}
                activeStep={0}
                handleStep={handleStep}
                stepperProps={{ sx: { maxWidth: '768px', m: 'auto' } }}
              />
              <AssignmentPublication
                data-testid="assignment-publication"
                session={session}
                handleStartAssignment={handleStartAssignment}
                handleValidation={() =>
                  handleStepValidation(step, session, cars)
                }
                readOnly={readOnly}
                contentIsReadOnly={userRoleHasReadOnly}
              />
            </>
          ) : step === ASSIGNMENT_STEPS_LIST[1] ? (
            <>
              {showHeader && (
                <Stepper
                  steps={steps}
                  activeStep={1}
                  handleStep={handleStep}
                  stepperProps={{ sx: { maxWidth: '768px', m: 'auto' } }}
                />
              )}
              <AssignmentCarAssignment
                data-testid="assignment-car-assignment"
                session={session}
                partners={partners}
                cars={cars}
                refetchList={refetchList}
                handleValidation={() =>
                  handleStepValidation(step, session, cars)
                }
                handleConfirm={() => confirmAssignment(session?.sessionId)}
                readOnly={readOnly}
                contentIsReadOnly={userRoleHasReadOnly}
                setShowHeader={setShowHeader}
              />
            </>
          ) : (
            <>
              <Stepper
                steps={steps}
                activeStep={2}
                handleStep={handleStep}
                stepperProps={{ sx: { maxWidth: '768px', m: 'auto' } }}
              />
              <AssignmentSummary
                data-testid="assignment-summary"
                session={session}
                partners={partners}
                cars={cars}
                handleValidation={() =>
                  handleStepValidation(step, session, cars)
                }
                handleConfirm={() => confirmAssignment(session?.sessionId)}
                readOnly={readOnly}
                contentIsReadOnly={userRoleHasReadOnly}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
